/**
 * タグ付け条件の型定義
 */

export interface TagCondition {
  id?: number;
  condition_column: string; // 項目（title, working_location など）
  condition_content: string; // キーワード
  condition_type: string; // 演算子（incl, excl など）
}

export const FIELD_OPTIONS = [
  { label: '求人タイトル', value: 'title' },
  { label: '求人本文', value: 'keyword_content' },
  { label: '勤務地', value: 'working_location' },
  { label: '職種名', value: 'job_position_name' },
  { label: '企業名', value: 'company_name' },
  { label: '企業選択', value: 'company_id' },
  { label: '年収', value: 'salary_range' },
];

export const OPERATOR_OPTIONS = [
  { label: 'が一致する', value: 'eq' },
  { label: 'のいずれかと完全一致（OR）', value: 'in' },
  { label: 'のいずれかを含む（OR）', value: 'incl' },
  { label: 'のすべてを含む（AND）', value: 'incl_all' },
  { label: 'を含まない', value: 'excl' },
];

/** 企業名条件の condition_column 値 */
export const COMPANY_NAME_COLUMN = 'company_name';

/** 企業名条件で選べる演算子（excl はバックエンドの企業名判定が未対応のため除外する） */
export const COMPANY_NAME_OPERATOR_OPTIONS = OPERATOR_OPTIONS.filter(
  (option) => option.value !== 'excl'
);

/** 企業選択条件の condition_column 値 */
export const COMPANY_ID_COLUMN = 'company_id';

/** 企業選択条件で選べる演算子（バックエンドは in のみ許可） */
export const COMPANY_ID_OPERATOR_OPTIONS = OPERATOR_OPTIONS.filter(
  (option) => option.value === 'in'
);

/** 項目に応じて選択できる演算子の一覧を返す */
export function operatorOptionsFor(column: string): typeof OPERATOR_OPTIONS {
  if (column === COMPANY_NAME_COLUMN) return COMPANY_NAME_OPERATOR_OPTIONS;
  if (column === COMPANY_ID_COLUMN) return COMPANY_ID_OPERATOR_OPTIONS;
  return OPERATOR_OPTIONS;
}

/** 条件が企業選択条件かどうかを判定する */
export function isCompanyIdCondition(c: TagCondition): boolean {
  return c.condition_column === COMPANY_ID_COLUMN;
}

/** company_id として許可する形式（正の整数のみ） */
const COMPANY_ID_INTEGER_RE = /^[1-9]\d*$/;

/** カンマ区切りの condition_content を company_id の配列に分解する。不正要素は除去する */
export function parseCompanyIds(content: string): number[] {
  return content
    .split(',')
    .map((v) => v.trim())
    .filter((v) => COMPANY_ID_INTEGER_RE.test(v))
    .map((v) => Number(v));
}

/** company_id の配列を重複除去のうえカンマ区切りの condition_content に変換する */
export function buildCompanyIdsContent(ids: number[]): string {
  return Array.from(new Set(ids)).join(',');
}

/** 企業選択条件が空（1社も選択されていない）かどうかを判定する */
export function isCompanyIdConditionEmpty(content: string): boolean {
  return parseCompanyIds(content).length === 0;
}

/** 改行またはカンマ区切りのキーワード入力を、保存用のカンマ区切り文字列に正規化する */
export function normalizeKeywordContent(content: string): string {
  return content
    .split(/\r\n|\n|,/)
    .map((v) => v.trim())
    .filter((v) => v !== '')
    .join(',');
}

/** 年収レンジ条件の condition_column 値 */
export const SALARY_RANGE_COLUMN = 'salary_range';

/** 年収レンジ条件の condition_type 値 */
export const SALARY_RANGE_TYPE = 'salary_range';

/**
 * "min,max" 形式の condition_content を分解する
 * - "500,800" → { min: '500', max: '800' }
 * - "500,"   → { min: '500', max: '' }
 * - ",800"   → { min: '',    max: '800' }
 * - ""       → { min: '',    max: '' }
 *
 * バックエンド同様「カンマちょうど1個」を前提とする。"500,800,900" のような
 * 不正値は末尾を黙って捨てて表示し編集→保存でサイレントに値が変わる恐れがあるため、
 * カンマが1個でない場合は空扱い（{ min: '', max: '' }）として無効化する。
 */
export function parseSalaryRange(content: string): { min: string; max: string } {
  const parts = content.split(',');
  if (parts.length !== 2) {
    return { min: '', max: '' };
  }
  return {
    min: parts[0] ?? '',
    max: parts[1] ?? '',
  };
}

/**
 * 下限・上限を "min,max" 形式の文字列に変換する
 */
export function buildSalaryRangeContent(min: string, max: string): string {
  return `${min},${max}`;
}

/**
 * 条件が年収レンジ条件かどうかを判定する
 */
export function isSalaryRangeCondition(c: TagCondition): boolean {
  return c.condition_column === SALARY_RANGE_COLUMN;
}

/** 年収値として許可する形式（0以上の整数のみ。バックエンドの /\A\d+\z/ と揃える） */
const SALARY_INTEGER_RE = /^\d+$/;

/**
 * 年収レンジ条件が空（下限・上限とも未入力）かどうかを判定する
 */
export function isSalaryRangeEmpty(content: string): boolean {
  const { min, max } = parseSalaryRange(content);
  return min.trim() === '' && max.trim() === '';
}

/**
 * 年収レンジ条件の content が有効かどうかを判定する（バックエンドの検証と整合）
 * - 形式は "下限,上限"（カンマちょうど1個）
 * - 下限・上限は0以上の整数のみ（小数・指数・負値・非数値は不可）
 * - 少なくとも一方が指定されている（両方空は false）
 * - 両方指定の場合は 下限 <= 上限
 */
export function isSalaryRangeValid(content: string): boolean {
  const parts = content.split(',');
  if (parts.length !== 2) return false;

  const min = parts[0].trim();
  const max = parts[1].trim();
  const minOk = min === '' || SALARY_INTEGER_RE.test(min);
  const maxOk = max === '' || SALARY_INTEGER_RE.test(max);
  if (!minOk || !maxOk) return false;
  if (min === '' && max === '') return false;
  if (min !== '' && max !== '' && Number(min) > Number(max)) return false;
  return true;
}
